import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../app/store';
import {
  selectCurrentTask,
  selectNavigationViaTaskRouter,
} from '../redux/features/taskRouter/taskRouter.selector';

const selectRestaurant = (state: RootState) => state.restaurant;

const selectActiveRestaurantDetails = createSelector(
  selectRestaurant,
  (restaurant) => restaurant.selectedRestaurantDetails
);
const selectActiveRestaurantCode = createSelector(
  selectActiveRestaurantDetails,
  (restaurantDetails) => restaurantDetails?.restaurantCode
);
const selectActiveRestaurantTimezone = createSelector(
  selectActiveRestaurantDetails,
  (restaurantDetails) => restaurantDetails?.timezone
);
const selectActiveRestaurantIsUnsupervisedAI = createSelector(
  selectActiveRestaurantDetails,
  (restaurantDetails) => restaurantDetails?.isUnsupervisedAi
);

const selectRestaurantsByUserRole = createSelector(
  selectRestaurant,
  (restaurant) => restaurant.restaurantsByUserRole
);
const selectRestaurantsById = createSelector(
  selectRestaurant,
  (restaurant) => restaurant.restaurantsById
);

const selectActiveRestaurantIsBilingual = createSelector(
  selectActiveRestaurantDetails,
  (restaurantDetails) => restaurantDetails?.isBilingual || false
);

const selectRestaurantAccessLevels = (state: RootState) =>
  state.restaurant.restaurantAccessLevels;

const selectedStageSelector = (state: RootState) =>
  state.restaurant.selectedStage;
const selectAIIndicator = (state: RootState) => state.restaurant.isAIEnabled;

const selectPrimaryRestaurantCode = createSelector(
  selectActiveRestaurantDetails,
  (restaurantDetails) => restaurantDetails?.primaryRestaurantCode
);

const selectTaskRouterStatus = createSelector(
  selectRestaurant,
  (restaurant) => restaurant.taskRouterStatus
);

/**
 * HITL-1459: show the "task router is ON" advisory only when the agent walked
 * onto this restaurant themselves. An agent handling a real task-router
 * assignment owns the order and must not be nagged about it.
 *
 * The status is only ever fetched on the dropdown path, so `isEnabled` alone
 * would very nearly do — the currentTaskId / navigationViaTaskRouter terms make
 * the intent explicit and hold the line if the fetch ever moves.
 */
const selectShowTaskRouterOnBanner = createSelector(
  selectTaskRouterStatus,
  selectActiveRestaurantCode,
  selectCurrentTask,
  selectNavigationViaTaskRouter,
  (status, restaurantCode, currentTaskId, navigationViaTaskRouter) =>
    Boolean(
      restaurantCode &&
        status?.isEnabled &&
        status.restaurantCode === restaurantCode &&
        !currentTaskId &&
        !navigationViaTaskRouter
    )
);

export {
  selectAIIndicator,
  selectActiveRestaurantCode,
  selectActiveRestaurantDetails,
  selectActiveRestaurantIsUnsupervisedAI,
  selectActiveRestaurantTimezone,
  selectPrimaryRestaurantCode,
  selectRestaurant,
  selectRestaurantAccessLevels,
  selectRestaurantsById,
  selectRestaurantsByUserRole,
  selectShowTaskRouterOnBanner,
  selectTaskRouterStatus,
  selectedStageSelector,
  selectActiveRestaurantIsBilingual,
};
